import { A } from '@solidjs/router';
import { createMemo, createSignal, onCleanup, onMount, Show } from 'solid-js';
import { locale, t } from '@/shared/i18n';
import { trackMarketingEvent } from '@/shared/lib/marketing-analytics';
import FeatureMarketingLayout from './components/FeatureMarketingLayout';
import FeatureFaqSection from './components/FeatureFaqSection';

export default function TeamPermissionsPage() {
  onMount(() => {
    const schema = {
      '@context': 'https://schema.org',
      '@graph': [
        {
          '@type': 'SoftwareApplication',
          name: 'khoai.io — Team & Permissions',
          applicationCategory: 'BusinessApplication',
          operatingSystem: 'Web, iOS, Android',
          url: 'https://khoai.io/features/team-permissions',
          description:
            'Role-based access control, granular permissions, and complete audit logs for team accountability across warehouses and stores.',
          offers: { '@type': 'Offer', price: '0', priceCurrency: 'USD' },
          publisher: {
            '@type': 'Organization',
            name: 'khoai.io',
            url: 'https://khoai.io',
          },
        },
        {
          '@type': 'BreadcrumbList',
          itemListElement: [
            {
              '@type': 'ListItem',
              position: 1,
              name: 'Home',
              item: 'https://khoai.io',
            },
            {
              '@type': 'ListItem',
              position: 2,
              name: 'Team & Permissions',
              item: 'https://khoai.io/features/team-permissions',
            },
          ],
        },
      ],
    };
    let script = document.getElementById(
      'ld-json-feature-team-permissions'
    ) as HTMLScriptElement | null;
    if (!script) {
      script = document.createElement('script');
      script.type = 'application/ld+json';
      script.id = 'ld-json-feature-team-permissions';
      document.head.appendChild(script);
    }
    script.textContent = JSON.stringify(schema);
    onCleanup(() =>
      document.getElementById('ld-json-feature-team-permissions')?.remove()
    );
  });

  const [rolesImageFailed, setRolesImageFailed] = createSignal(false);
  const [customRolesImageFailed, setCustomRolesImageFailed] =
    createSignal(false);
  const [auditLogImageFailed, setAuditLogImageFailed] = createSignal(false);
  const [inviteImageFailed, setInviteImageFailed] = createSignal(false);

  const localizedRolesImage = createMemo(() =>
    locale() === 'vi'
      ? '/team_roles_matrix_vn.jpg'
      : '/team_roles_matrix_en.jpg'
  );
  const localizedCustomRolesImage = createMemo(() =>
    locale() === 'vi'
      ? '/team_custom_roles_vn.jpg'
      : '/team_custom_roles_en.jpg'
  );
  const localizedAuditLogImage = createMemo(() =>
    locale() === 'vi' ? '/team_audit_log_vn.jpg' : '/team_audit_log_en.jpg'
  );
  const localizedInviteImage = createMemo(() =>
    locale() === 'vi'
      ? '/team_invite_members_vn.jpg'
      : '/team_invite_members_en.jpg'
  );

  return (
    <FeatureMarketingLayout
      pageTag={t('feature.team.tag')}
      title={t('feature.team.title')}
      subtitle={t('feature.team.subtitle')}
      lightTheme
      heroRevealOnScroll
    >
      {/* -- Section 1: Pre-built roles -- */}
      <section class="bg-amber-50/55 py-16">
        <div class="mx-auto grid max-w-7xl gap-8 px-4 sm:px-6 lg:grid-cols-[0.72fr_1.28fr] lg:items-center lg:px-8">
          <div class="p-2 sm:p-4 lg:pr-2">
            <h2 class="text-3xl font-black text-slate-900">
              {t('feature.team.f1')}
            </h2>
            <p class="mt-4 text-base leading-relaxed text-slate-700">
              {t('feature.team.f1Desc')}
            </p>
            <ul class="mt-6 list-inside list-disc space-y-3 text-sm text-slate-700">
              <li>{t('feature.team.f1Bullet1')}</li>
              <li>{t('feature.team.f1Bullet2')}</li>
              <li>{t('feature.team.f1Bullet3')}</li>
            </ul>
          </div>
          <div class="p-0 sm:p-2 lg:pl-2">
            <Show
              when={!rolesImageFailed()}
              fallback={
                <div class="flex min-h-[340px] w-full items-center justify-center rounded-2xl bg-slate-50/70 p-6 text-center text-sm text-slate-500">
                  {t('feature.team.visual1Caption')}
                </div>
              }
            >
              <img
                src={localizedRolesImage()}
                alt={t('feature.team.visual1Title')}
                class="block h-auto w-full rounded-2xl object-contain shadow-[0_18px_48px_rgba(15,23,42,0.12)]"
                loading="lazy"
                onError={() => setRolesImageFailed(true)}
              />
            </Show>
          </div>
        </div>
      </section>

      {/* -- Section 2: Custom roles -- */}
      <section class="bg-white py-16">
        <div class="mx-auto grid max-w-7xl gap-8 px-4 sm:px-6 lg:grid-cols-[1.28fr_0.72fr] lg:items-center lg:px-8">
          <div class="order-2 p-0 sm:p-2 lg:order-1 lg:pr-2">
            <Show
              when={!customRolesImageFailed()}
              fallback={
                <div class="flex min-h-[340px] w-full items-center justify-center rounded-2xl bg-slate-50/70 p-6 text-center text-sm text-slate-500">
                  {t('feature.team.visual3Caption')}
                </div>
              }
            >
              <img
                src={localizedCustomRolesImage()}
                alt={t('feature.team.visual3Title')}
                class="block h-auto w-full rounded-2xl object-contain shadow-[0_18px_48px_rgba(15,23,42,0.12)]"
                loading="lazy"
                onError={() => setCustomRolesImageFailed(true)}
              />
            </Show>
          </div>
          <div class="order-1 p-2 sm:p-4 lg:order-2 lg:pl-2">
            <h2 class="text-3xl font-black text-slate-900">
              {t('feature.team.f2')}
            </h2>
            <p class="mt-4 text-base leading-relaxed text-slate-700">
              {t('feature.team.f2Desc')}
            </p>
            <ul class="mt-6 list-inside list-disc space-y-3 text-sm text-slate-700">
              <li>{t('feature.team.f2Bullet1')}</li>
              <li>{t('feature.team.f2Bullet2')}</li>
              <li>{t('feature.team.f2Bullet3')}</li>
            </ul>
          </div>
        </div>
      </section>

      {/* -- Section 3: Audit log -- */}
      <section class="bg-amber-50/55 py-16">
        <div class="mx-auto grid max-w-7xl gap-8 px-4 sm:px-6 lg:grid-cols-[0.72fr_1.28fr] lg:items-center lg:px-8">
          <div class="p-2 sm:p-4 lg:pr-2">
            <h2 class="text-3xl font-black text-slate-900">
              {t('feature.team.f3')}
            </h2>
            <p class="mt-4 text-base leading-relaxed text-slate-700">
              {t('feature.team.f3Desc')}
            </p>
            <ul class="mt-6 list-inside list-disc space-y-3 text-sm text-slate-700">
              <li>{t('feature.team.f3Bullet1')}</li>
              <li>{t('feature.team.f3Bullet2')}</li>
              <li>{t('feature.team.f3Bullet3')}</li>
            </ul>
          </div>
          <div class="p-0 sm:p-2 lg:pl-2">
            <Show
              when={!auditLogImageFailed()}
              fallback={
                <div class="flex min-h-[340px] w-full items-center justify-center rounded-2xl bg-slate-50/70 p-6 text-center text-sm text-slate-500">
                  {t('feature.team.visual2Caption')}
                </div>
              }
            >
              <img
                src={localizedAuditLogImage()}
                alt={t('feature.team.visual2Title')}
                class="block h-auto w-full rounded-2xl object-contain shadow-[0_18px_48px_rgba(15,23,42,0.12)]"
                loading="lazy"
                onError={() => setAuditLogImageFailed(true)}
              />
            </Show>
          </div>
        </div>
      </section>

      {/* -- Section 4: Invite with email -- */}
      <section class="bg-white py-16">
        <div class="mx-auto grid max-w-7xl gap-8 px-4 sm:px-6 lg:grid-cols-[1.04fr_0.96fr] lg:items-center lg:px-8">
          <div class="order-2 p-0 sm:p-2 lg:order-1 lg:flex lg:justify-end lg:pr-2">
            <Show
              when={!inviteImageFailed()}
              fallback={
                <div class="flex min-h-[340px] w-full items-center justify-center rounded-2xl bg-slate-50/70 p-6 text-center text-sm text-slate-500">
                  {t('feature.team.visual4Caption')}
                </div>
              }
            >
              <img
                src={localizedInviteImage()}
                alt={t('feature.team.visual4Title')}
                class="block h-auto w-full max-w-[420px] rounded-2xl object-contain shadow-[0_18px_48px_rgba(15,23,42,0.12)]"
                loading="lazy"
                onError={() => setInviteImageFailed(true)}
              />
            </Show>
          </div>
          <div class="order-1 p-2 sm:p-4 lg:order-2 lg:pl-2">
            <h2 class="text-3xl font-black text-slate-900">
              {t('feature.team.f4')}
            </h2>
            <p class="mt-4 text-base leading-relaxed text-slate-700">
              {t('feature.team.f4Desc')}
            </p>
            <ul class="mt-6 list-inside list-disc space-y-3 text-sm text-slate-700">
              <li>{t('feature.team.f4Bullet1')}</li>
              <li>{t('feature.team.f4Bullet2')}</li>
              <li>{t('feature.team.f4Bullet3')}</li>
            </ul>
          </div>
        </div>
      </section>

      {/* -- CTA -- */}
      <section class="bg-white py-16">
        <div class="mx-auto max-w-7xl px-4 text-center sm:px-6 lg:px-8">
          <h2 class="text-4xl font-black text-slate-900">
            {t('feature.team.cta')}
          </h2>
          <p class="mx-auto mt-4 max-w-2xl text-base text-slate-700">
            {t('feature.team.ctaSubtitle')}
          </p>
          <A
            href="/signup"
            onClick={() =>
              trackMarketingEvent('feature_cta_click', {
                feature: 'team-permissions',
                target: '/signup',
              })
            }
            class="mt-8 inline-block rounded-xl bg-amber-600 px-8 py-3.5 text-lg font-bold text-white hover:bg-amber-700"
          >
            {t('feature.cta')}
          </A>
        </div>
      </section>

      <FeatureFaqSection
        titleKey="feature.team.faq.title"
        scriptId="ld-json-team-faq"
        items={[
          { q: t('feature.team.faq.q1'), a: t('feature.team.faq.a1') },
          { q: t('feature.team.faq.q2'), a: t('feature.team.faq.a2') },
          { q: t('feature.team.faq.q3'), a: t('feature.team.faq.a3') },
          { q: t('feature.team.faq.q4'), a: t('feature.team.faq.a4') },
          { q: t('feature.team.faq.q5'), a: t('feature.team.faq.a5') },
        ]}
      />
    </FeatureMarketingLayout>
  );
}
